import { CollideEvent, CollideId, isOneHitCollider } from '../../defs/collide';
import type { AlarmBlockEntity } from '../../entities/BlockEntity';
import { despawnBlockEntity } from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { HitEffect } from '../effects/HitEffect';
import { InvalidEffect } from '../effects/InvalidEffect';
import { SmokeEffect } from '../effects/SmokeEffect';
import { CollideHandler, ICollision } from './ICollision';

/*
    alarm block collision resolver
*/
export class AlarmCollision implements ICollision {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _entity: AlarmBlockEntity;
  // map
  private readonly _resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this._resolveTap,
    block: this._resolveBlock,
    attack: this._resolveAttack,
  };

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, entity: AlarmBlockEntity) {
    this._scene = scene;
    this._entity = entity;
  }

  // impl
  //-------------------------------------------------------------------------
  public async resolve(event: CollideEvent): Promise<boolean> {
    return this._resolvers[event.id]?.call(this, event) ?? false;
  }

  // private: resolvers
  //-------------------------------------------------------------------------
  private async _resolveTap(event: CollideEvent) {
    // invalid effect
    await new InvalidEffect(this._entity).execute();
    return false;
  }

  private async _resolveBlock(event: CollideEvent) {
    const collider = event.collider;

    // attack if basic block collider
    if (
      collider?.type === 'block' &&
      collider.entity.c.block.props.type === 'basic'
    ) {
      await this._attack();
      return true;
    }
    return false;
  }

  private async _resolveAttack(event: CollideEvent) {
    if (isOneHitCollider(event.collider?.type)) {
      this._entity.c.blockAlarm.count = 0;
    }
    await this._attack();
    return true;
  }

  // private: actions
  //-------------------------------------------------------------------------
  private async _attack() {
    const entity = this._entity;
    const position = entity.c.position.mapPosition;
    const component = entity.c.blockAlarm;

    // if count is 0
    if (component.count <= 0) {
      // despawn block
      despawnBlockEntity(this._scene, this._entity);

      // execute break effect
      await new SmokeEffect(this._scene, { position }).execute();

      // else decrement count
    } else {
      --component.count;
      this._scene?.events.publish({ id: 'poke' });
      await new HitEffect(this._scene, { position }).execute();
    }
  }
}
